import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { PaxDto, PaxResponseDto } from '@bandido/shared/dtos';
import { fromDDMMYYYYToDate, fromDDMMYYYYToYYYYMMDD, fromYYYYMMDDToDDMMYYYY } from '@frontend/features/common/helper';
import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import { CartPaxForm } from './form';
import { useAddPaxesToSaleOrder } from '../../queries';

type Props = {
  isOpen: boolean;
  onBack: () => void;
  onContinue: () => void;
  saleOrderId: string;
  pax: PaxResponseDto | null;
}

const useValidationSchema = () => {
  const { t } = useTranslation();
  return yup.object().shape({
    id: yup.string().optional(),
    name: yup.string().required(t('hotels.fares.hotelPaxData.name.required')),
    lastName: yup.string().required(t('hotels.fares.hotelPaxData.lastName.required')),
    dob: yup.string()
      .required(t('hotels.fares.hotelPaxData.birthDate.required'))
      .test(
        'is-valid-date',
        t('hotels.fares.hotelPaxData.birthDate.notValid'),
        (value) => !!value && fromYYYYMMDDToDDMMYYYY(value) !== null
      )
      .test(
        'is-past-date',
        t('hotels.fares.hotelPaxData.birthDate.notPast'),
        (value) => {
          const date = fromDDMMYYYYToDate(value || '');
          return !!date && date <= new Date();
        }
      ),
    email: yup.string()
      .email(t('hotels.fares.hotelPaxData.email.notValid'))
      .optional(),
    phone: yup.string()
      .optional(),
  });
};

const defaultPax: PaxDto = {
  id: '',
  name: '',
  lastName: '',
  dob: '',
  email: '',
  phone: '',
};

export const HotelPaxForm = ({
  onBack,
  onContinue,
  isOpen,
  saleOrderId,
  pax
}: Props) => {
  const formRef = useRef<HTMLFormElement>(null);
  const validationSchema = useValidationSchema();
  const queryClient = useQueryClient();
  const { mutate: addPaxesToSaleOrder, isLoading: isAddingPaxes } = useAddPaxesToSaleOrder();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();

  const {
    control,
    formState,
    handleSubmit,
    register,
    setValue,
    reset
  } = useForm<PaxDto>({
    defaultValues: defaultPax,
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = (data: PaxDto) => {
    addPaxesToSaleOrder({
      id: saleOrderId,
      paxes: [{...data, dob: fromDDMMYYYYToYYYYMMDD(data.dob)}],
      onSuccess: async () => {
        await queryClient.invalidateQueries([QueryKeys.getSaleOrdersDetails, saleOrderId]);
        await queryClient.invalidateQueries([QueryKeys.getSaleOrdersPaxes, saleOrderId]);
        addToast({
          message: t('saleOrder.view.passengers.successUpdatingPax'),
          color: 'success',
        });
        onContinue();
      },
      onError: () => {
        addToast({
          message: t('saleOrder.view.passengers.errorUpdatingPax'),
          color: 'error',
        });
      },
    });
  };

  const onClose = () => {
    reset();
    onBack();
  };

  useEffect(() => {
    reset(pax ? {
      id: pax.id,
      name: pax.name,
      lastName: pax.lastName,
      dob: fromYYYYMMDDToDDMMYYYY(pax.dob),
      email: pax.email,
      phone: pax.phone,
    } : defaultPax);
  }, [pax]);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Dialog open={isOpen} onClose={onClose}>
        <DialogTitle>{t('saleOrder.view.passengers.newPassenger')}</DialogTitle>
        <DialogContent sx={{ minWidth: '450px' }}>
          <Box display="flex" flexDirection="column" gap={3}> 
            <Typography variant="subtitle2">
              {t('saleOrder.view.passengers.passengerData')}
            </Typography>
            <CartPaxForm
              register={register}
              setValue={setValue}
              control={control}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            variant="outlined"
          >
            {t('common.cancel')}
          </Button>
          <LoadingButton
            loading={isAddingPaxes}
            variant="contained"
            type="button"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={!formState.isValid}
          >
            {t('common.continue')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </form>
  );
};
